import {
  List,
  Datagrid,
  TextField,
  DateField,
  SearchInput,
  SelectInput,
  ReferenceInput,
  ReferenceField,
  useRecordContext,
  TopToolbar,
  CreateButton,
  usePermissions,
} from 'react-admin';
import { Chip } from '@mui/material';
import type { Build, BuildStatus } from '../../types';
import { TemplateVersionField } from './TemplateVersionField';

const buildFilters = [
  <SearchInput key="q" source="q" alwaysOn />,
  <ReferenceInput key="clientId" source="clientId" reference="clients">
    <SelectInput optionText="name" />
  </ReferenceInput>,
  <SelectInput
    key="status"
    source="status"
    choices={[
      { id: 'queued', name: 'Queued' },
      { id: 'preparing', name: 'Preparing' },
      { id: 'in_progress', name: 'In Progress' },
      { id: 'building', name: 'Building' },
      { id: 'uploading', name: 'Uploading' },
      { id: 'publishing', name: 'Publishing' },
      { id: 'completed', name: 'Completed' },
      { id: 'failed', name: 'Failed' },
      { id: 'timeout', name: 'Timeout' },
    ]}
  />,
  // Feature 012
  <ReferenceInput key="templateKey" source="templateKey" reference="templates">
    <SelectInput optionText="name" optionValue="key" label="Template" />
  </ReferenceInput>,
  <SelectInput
    key="triggerType"
    source="triggerType"
    choices={[
      { id: 'manual', name: 'Manual' },
      { id: 'config_change', name: 'Config Change' },
      { id: 'core_update', name: 'Core Update' },
      { id: 'mass_rebuild', name: 'Mass Rebuild' },
    ]}
  />,
];

const StatusField = () => {
  const record = useRecordContext<Build>();
  if (!record) return null;

  const statusColors: Record<
    BuildStatus,
    'default' | 'info' | 'warning' | 'success' | 'error'
  > = {
    queued: 'default',
    preparing: 'info',
    in_progress: 'warning',
    building: 'warning',
    uploading: 'info',
    publishing: 'info',
    completed: 'success',
    failed: 'error',
    timeout: 'error',
  };

  return (
    <Chip
      label={record.status}
      color={statusColors[record.status]}
      size="small"
    />
  );
};

const ListActions = () => {
  const { permissions } = usePermissions();

  return (
    <TopToolbar>
      {permissions?.includes('builds:write') && <CreateButton />}
    </TopToolbar>
  );
};

export const BuildList = () => (
  <List
    filters={buildFilters}
    actions={<ListActions />}
    sort={{ field: 'createdAt', order: 'DESC' }}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <ReferenceField source="clientId" reference="clients" label="Client" link="show">
        <TextField source="name" />
      </ReferenceField>
      <TemplateVersionField label="Template" />
      <StatusField />
      <TextField source="triggerType" />
      <DateField source="createdAt" showTime />
      <DateField source="completedAt" showTime />
    </Datagrid>
  </List>
);
